import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import { Modal } from 'react-bootstrap-5';

export default function BuildoutModal({ className, centered = true, onExit, onExited, ...props }) {
  const modalElement = useRef(null);

  const handleExit = useCallback((modal) => {
    modal.addEventListener('transitionend', stopDescendantTransitionEndBubble, false);
    modalElement.current = modal;
    onExit?.(modal);
  }, [onExit]);

  const handleExited = useCallback((modal) => {
    if (modalElement.current) {
      modalElement.current.removeEventListener('transitionend', stopDescendantTransitionEndBubble, false);
      modalElement.current = null;
    }
    onExited?.(modal);
  }, [onExited]);

  return (
    <Modal
      centered={centered}
      className={className}
      onExit={handleExit}
      onExited={handleExited}
      {...props}
    />
  );
}

BuildoutModal.propTypes = {
  centered: PropTypes.bool,
  className: PropTypes.string,
  fullscreen: PropTypes.string,
  size: PropTypes.string,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
};

BuildoutModal.Body = Modal.Body;
BuildoutModal.Footer = Modal.Footer;
BuildoutModal.Header = function Header({ children, ...props }) {
  return (
    <Modal.Header {...props}>
      <Modal.Title className="fs-6">{children}</Modal.Title>
    </Modal.Header>
  );
};

BuildoutModal.Header.propTypes = {
  children: PropTypes.node,
};

// This is a workaround to a react-bootstrap modal bug:
// 1. If the user has animations turned off at the OS level...
// 2. And a modal renders a child component with CSS transitions not affected by the OS setting...
// 3. And that component triggers its transition at the same time that the modal is closed...
// 4. Then, there is a race condition where sometimes the modal will become transparent but remain
//    in the DOM and intercept all mouse events, making the page unresponsive
//
// Bootstrap modals normally remove themselves from the DOM after the fadeout animation completes.
// If that animation is disabled, a fallback cleans up after a short delay. However, detection of
// the enabled/disabled state of the animation is bugged - if *any* CSS transition end event bubbles
// up from the modal, even one of its children, the fallback disables itself, thinking the modal
// will handle the event. However, the modal correctly ignores the event as it is for some other
// element, leaving the page in a bad state.
//
// One example is a styled input - if it has a different border color applied when focused, and it
// has a CSS transition on the border property to animated it, and it blurs at the same time a modal
// stops closing (e.g. it was focused when the user clicked "cancel"), then you can hit the bug.
//
// The workaround prevents such non-modal transition events from bubbling up into the fallback,
// ensuring it does not detect modal animations as enabled when they actually are not.
//
// Relevant links:
// * https://github.com/react-bootstrap/react-bootstrap/issues/6153
// * https://github.com/react-bootstrap/dom-helpers/issues/184
function stopDescendantTransitionEndBubble(e) {
  if (e.target !== e.currentTarget) {
    e.stopImmediatePropagation();
  }
}
